import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import { SEOService } from '@app/core/services/seo.service';
import * as fromRoot from '@app/reducers';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { getPathWithoutLastFragment } from '@app/shared/helpers/location';
import { Identity } from '@app/shared/models/identity';
import { DeliveryType, EmailDelivery, LinkDelivery } from '@app/shared/models/transferConfiguration';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { Observable, Subscription } from 'rxjs';
import { filter, first, withLatestFrom } from 'rxjs/operators';
import { RawFileError } from '@app/client/uploader/interfaces/file';
import { LayoutActions, PrepareActions } from '@app/client/uploader/store/actions';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { ModalFatalUploadErrorComponent } from '../send/modals/modal-fatal-upload-error/modal-fatal-upload-error.component';
import { ModalForbiddenEmailComponent } from './modals/modal-forbidden-email/modal-forbidden-email.component';
import { ModalForbiddenSubscriptionComponent } from './modals/modal-forbidden-subscription/modal-forbidden-subscription.component';
import { ModalPasswordRequiredComponent } from './modals/modal-password-required/modal-password-required.component';
import { ScrollPrepareService } from '@app/core/services/scroll-prepare.service';
import { UploaderError, UploaderStatus } from '@smash-sdk/uploader';
import { CloudfrontError } from '@app/shared/helpers/cloudfront';
import { ErrorDetails, RECOVERABLE_INLINE_FORM_ERROR_NAMES, RECOVERABLE_MODAL_ANSWERABLE_ERROR_NAMES } from '@app/shared/helpers/uploader';

@Component({
  selector: 'app-prepare',
  templateUrl: './prepare.component.html',
  styleUrls: ['./prepare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrepareComponent implements OnInit, OnDestroy {

  dropbox$: Observable<any>;
  identity$: Observable<Identity>;
  rawFiles$: Observable<FileSystemMetadata[]>;
  totalSizeToUpload$: Observable<number>;
  uploadFormMode$: Observable<string>;
  domain$: Observable<GetRecordOutput['record']>;
  canConfigSmash$: Observable<boolean>;
  canConfigDropboxTransfer$: Observable<boolean>;
  transferSettings$: Observable<any>;
  rawFilesError$: Observable<RawFileError>;
  uploadError$: Observable<any>;
  updateAndResumeUploadError$: Observable<any>;
  subscriptions: Subscription[] = [];
  uploadStatus$: Observable<UploaderStatus>;

  lastScrollPosition = 0;
  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private router: Router,
    private rawFileStoreService: RawFileStoreService,
    private dialog: DialogService,
    private route: ActivatedRoute,
    private seoService: SEOService,
    private scrollPrepare: ScrollPrepareService,
    private readonly localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {

    if (isPlatformBrowser(this.platformId)) {
      this.store$.dispatch(PrepareActions.resetDrawer());
      this.dropbox$ = this.store$.select(fromUploader.getDropboxPreview);
      this.rawFiles$ = this.store$.select(fromUploader.getAllRawFiles);
      this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
      this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
      this.uploadFormMode$ = this.store$.select(fromUploader.getUploadFormMode);
      this.totalSizeToUpload$ = this.store$.select(fromUploader.getTransferSize);
      this.uploadError$ = this.store$.select(fromUploader.getUploadError);
      this.updateAndResumeUploadError$ = this.store$.select(fromUploader.getUpdateAndResumeUploadError);
      this.rawFilesError$ = this.store$.select(fromUploader.getRawFilesError);
      this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);
      if (this.isDropboxId) { // TO DO find a better way
        this.canConfigSmash$ = this.store$.select(fromUploader.getCanConfigureDropboxTransfer)
      } else {
        this.canConfigSmash$ = this.store$.select(fromRoot.canConfigSmash);
      }
      this.transferSettings$ = this.store$.select(fromRoot.getActiveTransferSettings(this.isDropboxId));


      this.subscriptions.push(
        this.rawFiles$.subscribe(files => {
          if (!files.length) {
            this.router.navigate([getPathWithoutLastFragment()], { relativeTo: this.route });
          }
        }),
        this.uploadStatus$.subscribe(uploadStatus => {
          // Guard against re-navigating when already on the send route (parity with the mobile
          // uploader's `currentStep !== 'SEND'` guard): the status can change several times
          // (e.g. Started → NetworkPaused → Started) while this subscription is alive.
          //
          // UploaderStatus.Error is deliberately NOT handled here: the SDK's own `status` event
          // listener (upload.effects.ts setupUploaderListeners()) dispatches SetUploadStatus(Error)
          // independently of — and typically before — the catchError block that dispatches the
          // paired UploadFailure(error) carrying the actual error object (upload.effects.ts
          // ~l.87-97). Deciding whether to navigate right here, off this uploadStatus$ emission,
          // would race: uploadError$ would still hold the previous attempt's (or no) error, so a
          // recoverable, modal-answerable error could never be told apart from a fatal one. The
          // uploadError$ subscription below always fires with the real, current error already in
          // the store, so the Error case is handled entirely there instead.
          if ((uploadStatus === UploaderStatus.Started || uploadStatus === UploaderStatus.NetworkPaused) && !this.router.url.includes('/send')) {
            this.router.navigate(['../send'], { relativeTo: this.route });
          }
        }),
        this.uploadError$.pipe(filter(error => error !== null)).subscribe((error) => {
          // Inline-form errors (CustomUrlAlreadyInUseError) are surfaced by link-form.component.html's
          // field message off this same uploadError$; routing them through handleUploadError would
          // open the generic fatal modal on top. Skip it here -- this is the create-transfer path
          // where the inline renderer is mounted. The updateAndResumeUploadError$ subscription below
          // still routes EVERYTHING through handleUploadError, since that path has no inline renderer,
          // so an error there can never go silent.
          if (!RECOVERABLE_INLINE_FORM_ERROR_NAMES.has(error?.name)) {
            this.handleUploadError(error);
          }
          // Recoverable, modal-answerable errors (PasswordRequiredError, EmailNotAllowedError,
          // InvalidSubscriptionError) must NOT navigate away: handleUploadError() above already
          // opened the matching modal, and navigating to /send destroys the prepare-screen
          // components the modal's resubmit path depends on (e.g. #button-send-transfer-link),
          // silently dropping whatever the user just entered (the mandatory-password bug).
          // Recoverable, inline-form errors (CustomUrlAlreadyInUseError) must likewise NOT
          // navigate away: link-form.component.html already renders the UPLOADER_LINK_ALREADY_EXISTS
          // message off this same uploadError$, but only while the link form stays mounted --
          // navigating to /send destroys it before the user ever sees the message (the "add a
          // title -> bounced to home, transfer never sent" bug). Every other error (generic
          // S3/network failures, DropboxDisabledError, TransferAlreadyQueuedError, ...) keeps
          // navigating to /send exactly as before.
          if (
            !RECOVERABLE_MODAL_ANSWERABLE_ERROR_NAMES.has(error?.name)
            && !RECOVERABLE_INLINE_FORM_ERROR_NAMES.has(error?.name)
            && !this.router.url.includes('/send')
          ) {
            this.router.navigate(['../send'], { relativeTo: this.route });
          }
        }),
        // A failed UpdateAndResumeUpload (queue-resume "send anyway", password-modal resubmit) was
        // previously completely silent: the reducer stores the error but nothing ever read it, so a
        // failure (permission-denied, a locked/expired transfer, a transient 5xx, ...) just stopped
        // the spinner and left the user back on the same prepare screen with zero explanation. Route
        // it through the same handleUploadError() used for the create-transfer flow -- the user is
        // already on prepare at this point (this action fires mid-flow, from a modal), so no
        // navigation is needed, only surfacing the error.
        this.updateAndResumeUploadError$.pipe(filter(error => error !== null)).subscribe((error) => {
          this.handleUploadError(error);
        }),
      );
    }
  }

  get isDropboxId() { // TO DO find a better way
    return !!(this.route.snapshot.paramMap.get('dropboxId'));
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadDeliveryParameters();
      this.loadDeliveryParametersFromQueryString();
      this.seoService.setLandingTitle();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.scrollPrepare.scrollDirection.next('down');
  }

  fileOver() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
  }

  fileLeave() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
  }

  // Set by (entryReadError), captured+reset synchronously at the top of dropped() -- setFiles()'s
  // own resetErrors() would otherwise wipe an error dispatched any earlier, and reading this flag
  // only AFTER awaiting setFiles() would risk a second, overlapping drop's entryReadError
  // overwriting it before this drop's check runs. See onEntryReadError().
  private hadEntryReadError = false;

  onEntryReadError(): void {
    this.hadEntryReadError = true;
  }

  async dropped(files: FileDropEntry[]) {
    const hadEntryReadError = this.hadEntryReadError;
    this.hadEntryReadError = false;
    // tslint:disable-next-line:no-shadowed-variable
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    await this.rawFileStoreService.setFiles(files, identity);
    if (hadEntryReadError) {
      this.rawFileStoreService.reportRelativePathTooLongError();
    }
  }

  async openModalForbiddenEmail({ requestId }: { requestId: string }): Promise<void> {
    // tslint:disable-next-line:no-shadowed-variable
    const domain = await this.domain$.pipe(first(domain => !!domain)).toPromise();
    this.dialog.open(ModalForbiddenEmailComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'xs',
        data: {
          domain,
          requestId,
        },
      }
    );
  }

  openModalForbiddenSubscription({ requestId }: { requestId: string }): void {
    this.dialog.open(ModalForbiddenSubscriptionComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'sm',
        data: {
          requestId,
        },
      });
  }

  openModalPasswordRequired({ requestId }: { requestId: string }): void {
    this.dialog.open(ModalPasswordRequiredComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'xs',
        data: {
          requestId,
        },
      });
  }

  onFileOver(over) {
    if (over) {
      this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
    } else {
      this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
    }
  }

  loadDeliveryParameters() {
    this.identity$.pipe(
      first(),
      withLatestFrom(this.domain$),
    ).subscribe(([identity, domain]) => {
      const lastDeliveryParameters = this.getLastDeliveryParameters();

      if (this.isDropboxId) {
        // If upload on dropbox force the uploadMode to Email to handle subject && description
        this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode: "Email" }));
      } else if (lastDeliveryParameters?.type && typeof lastDeliveryParameters?.type === 'string') {
        // Set the upload form mode based on the last delivery parameters or the default value if the last delivery parameters are not valid anymore.
        // Example : if the user has previously selected a delivery type that is not available anymore.
        const options = domain?.options?.transfer?.delivery?.options as DeliveryType[];
        // FIX ME It should be the default value from the domain but it is not the correct one for now.
        // Example : admin remove the email delivery option from the domain. The default value should be the link delivery. But it is not the case.
        const defaultUploadFormMode = domain?.options?.transfer?.delivery?.options[0] as DeliveryType;
        const uploadFormMode = options?.includes(lastDeliveryParameters?.type) ? lastDeliveryParameters?.type : defaultUploadFormMode;
        this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
      }

      if (identity?.username) {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: { email: identity?.username } }));
      } else if (lastDeliveryParameters?.sender) {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: lastDeliveryParameters?.sender }));
      }
    });
  }

  getLastDeliveryParameters(): EmailDelivery | LinkDelivery | undefined {
    const lastDeliveryParametersString: string = this.localStorageService.getItem('lastTransferParameters');
    if (lastDeliveryParametersString) {
      return JSON.parse(decodeURIComponent(escape(atob(lastDeliveryParametersString)))) as EmailDelivery | LinkDelivery;
    } else {
      return undefined;
    }
  }

  loadDeliveryParametersFromQueryString() {
    this.identity$.pipe(
      first(),
      withLatestFrom(this.domain$),
    ).subscribe(([identity, domain]) => {
      const prefilledTransferParametersString: string = this.localStorageService.getItem('prefilledTransferParameters');
      if (prefilledTransferParametersString) {
        const params = JSON.parse(prefilledTransferParametersString);
        if (params) {
          const { sender, name, receivers, uploadFormMode, description, title } = params;
          const options = domain?.options?.transfer?.delivery?.options as DeliveryType[] || [];
          // A drop-link upload always forces 'Email' mode (see loadDeliveryParameters() above,
          // and the mirrored guard in the mobile prepare-mobile.component.ts). A stale or foreign
          // 'type=Link' query parameter carried on the dropbox URL must never flip it back to
          // 'Link': the upload-parameters-box template always renders the email form for a drop
          // link regardless of uploadFormMode, so a 'Link' mode here only swaps the footer to the
          // Link submit button while no #linkForm was ever instantiated -- clicking it crashes on
          // `this.linkForm.onSubmit(...)` (linkForm is undefined).
          if (!this.isDropboxId && uploadFormMode && options.includes(uploadFormMode as DeliveryType)) {
            this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
          }
          if (sender && !identity?.username) {
            this.store$.dispatch(PrepareActions.SetTransferSender({ sender: { email: sender, name } }));
          }
          if (description) {
            this.store$.dispatch(PrepareActions.SetEmailDescription({ description }));
          }
          if (title) {
            this.store$.dispatch(PrepareActions.SetEmailTitle({ title }));
            this.store$.dispatch(PrepareActions.SetLinkTitle({ title }));
          }
          if (receivers) {
            this.store$.dispatch(PrepareActions.SetEmailReceivers({ receivers }));
          }
        }
      }
    });
  }

  openModalUploadFatalError({ errorName, errorMessage, errorId, errorDetails }: { errorName?: string, errorMessage?: string, errorId?: string; errorDetails?: ErrorDetails }): void {
    this.dialog.open(ModalFatalUploadErrorComponent,
      {
        closeButton: false,
        enableClose: false,
        size: 'xs',
        data: {
          errorName,
          errorMessage,
          errorId,
          errorDetails,
        },
      });
  }

  // FIX ME we need to use the errorName class attribute from uploader errors constructor when it is implemented
  private handleUploadError(error: CloudfrontError<UploaderError>) {
    if (error?.name === 'EmailNotAllowedError') {
      this.openModalForbiddenEmail({ requestId: error?.requestId });
    } else if (error?.name === 'InvalidSubscriptionError') {
      this.openModalForbiddenSubscription({ requestId: error?.requestId });
    } else if (error?.name === 'PasswordRequiredError') {
      this.openModalPasswordRequired({ requestId: error?.requestId });
    } else if (error?.name === 'TransferAlreadyQueuedError') {
      this.store$.dispatch(PrepareActions.DisplayModalSizePremium());
    } else if (error?.name === 'DropboxDisabledError') {
      this.openModalUploadFatalError({
        errorName: error?.name,
        errorMessage: error?.message,
        errorId: error?.errorId,
        errorDetails: error?.details,
      });
    } else if (error) {
      // Fallback: any error name not specifically handled above (e.g. ForbiddenError,
      // UnauthorizedError, TransferAlreadyLockedError, a generic 5xx, ...) used to be silently
      // dropped -- the caller (uploadError$ or updateAndResumeUploadError$) would just stop its
      // spinner with no explanation. Surface it via the same generic fatal-error modal already
      // used for DropboxDisabledError, so no error can be entirely invisible to the user.
      //
      // Note: inline-form errors (CustomUrlAlreadyInUseError) never reach here from the create path
      // -- the uploadError$ subscription filters them out before calling handleUploadError, since
      // link-form.component.html already renders their field message. They CAN reach here from
      // updateAndResumeUploadError$ (no inline renderer on that path), where a visible fatal modal is
      // the correct fallback rather than silence.
      this.openModalUploadFatalError({
        errorName: error?.name,
        errorMessage: error?.message,
        errorId: error?.errorId,
        errorDetails: error?.details,
      });
    }
  }

  onSmallDeviceScroll(event) {
    if (event.target.scrollTop) {
      this.scrollPrepare.scrollDirection.next('up');
    } else {
      this.scrollPrepare.scrollDirection.next('down');
    }
  }
}
